import type { CountMessageVariants } from "../../utils/countMessage";
import { ContentAction } from "./types/conversation";

export const MAIL_SEARCH_COMMENT: CountMessageVariants = {
    empty: "Je n'ai trouvé aucun mail correspondant à vos critères de recherche.",
    singular: "J'ai trouvé 1 mail correspondant à votre recherche.",
    plural: "J'ai trouvé {count} mails correspondant à votre recherche.",
};

export const MAIL_SEARCH_VOCAL: CountMessageVariants = {
    empty: "Je n'ai trouvé aucun email correspondant à votre recherche.",
    singular: "J'ai trouvé 1 email qui correspond à votre recherche.",
    plural: "J'ai trouvé {count} emails qui correspondent à votre recherche.",
};

/**
 * Aucun mail, et des noms que les contacts ne résolvent pas (F-1.14) : on les cite, au lieu d'une
 * liste vide sans explication.
 */
export const noMailForUnknownContactsMessage = (names: string[]): string =>
    `Je n'ai trouvé aucun mail : aucun contact ne correspond à ${names.join(" ni à ")}.`;

export const AGENDA_ITEMS_COMMENT: CountMessageVariants = {
    empty: "Je n'ai trouvé aucun évènement correspondant à vos critères de recherche.",
    singular: "J'ai trouvé 1 événement pour la période demandée.",
    plural: "J'ai trouvé {count} événements pour la période demandée.",
};

export const AGENDA_ITEMS_VOCAL: CountMessageVariants = {
    empty: "Je n'ai trouvé aucun évènement correspondant à vos critères de recherche.",
    singular: "J'ai trouvé 1 événement dans votre agenda pour cette période.",
    plural: "J'ai trouvé {count} événements dans votre agenda pour cette période.",
};

export const FREE_SLOTS_COMMENT: CountMessageVariants = {
    empty: "Aucun créneau libre d'au moins {duration} n'a été trouvé pour la période demandée.",
    singular: "J'ai trouvé 1 créneau libre d'au moins {duration}.",
    plural: "J'ai trouvé {count} créneaux libres d'au moins {duration}.",
};

export const FREE_SLOTS_VOCAL: CountMessageVariants = {
    empty: "Je n'ai trouvé aucun créneau libre d'au moins {duration} pour cette période.",
    singular:
        "J'ai trouvé 1 créneau libre d'au moins {duration} dans votre agenda.",
    plural: "J'ai trouvé {count} créneaux libres d'au moins {duration} dans votre agenda.",
};

/** Action groupée : toute action sur une liste de mails, hors consultation. */
type GroupAction = Exclude<ContentAction, ContentAction.VIEW>;

/** Texte lu d'une action groupée (F-1.8), sans mail : rien n'est proposé. */
const NOTHING_TO_PROPOSE = MAIL_SEARCH_VOCAL.empty;

/**
 * Texte lu d'une action groupée (F-1.8) : l'action est proposée, l'utilisateur la confirme à l'écran.
 * Il ne dit jamais « je supprime » : rien n'est fait avant la confirmation.
 */
export const ACTION_PROPOSAL_VOCAL: Record<GroupAction, CountMessageVariants> =
    {
        [ContentAction.DELETE]: {
            empty: NOTHING_TO_PROPOSE,
            singular:
                "Je vous propose de supprimer 1 mail, confirmez à l'écran.",
            plural: "Je vous propose de supprimer {count} mails, confirmez à l'écran.",
        },
        [ContentAction.MARKASREAD]: {
            empty: NOTHING_TO_PROPOSE,
            singular:
                "Je vous propose de marquer 1 mail comme lu, confirmez à l'écran.",
            plural: "Je vous propose de marquer {count} mails comme lus, confirmez à l'écran.",
        },
        [ContentAction.MARKASUNREAD]: {
            empty: NOTHING_TO_PROPOSE,
            singular:
                "Je vous propose de marquer 1 mail comme non lu, confirmez à l'écran.",
            plural: "Je vous propose de marquer {count} mails comme non lus, confirmez à l'écran.",
        },
        [ContentAction.MARKASUSERPROCESSED]: {
            empty: NOTHING_TO_PROPOSE,
            singular:
                "Je vous propose de marquer 1 mail comme traité, confirmez à l'écran.",
            plural: "Je vous propose de marquer {count} mails comme traités, confirmez à l'écran.",
        },
        [ContentAction.MARKASUSERUNPROCESSED]: {
            empty: NOTHING_TO_PROPOSE,
            singular:
                "Je vous propose de marquer 1 mail comme non traité, confirmez à l'écran.",
            plural: "Je vous propose de marquer {count} mails comme non traités, confirmez à l'écran.",
        },
        [ContentAction.MARKASFOCUS]: {
            empty: NOTHING_TO_PROPOSE,
            singular:
                "Je vous propose de déplacer 1 mail dans Focus, confirmez à l'écran.",
            plural: "Je vous propose de déplacer {count} mails dans Focus, confirmez à l'écran.",
        },
        [ContentAction.MARKASUNFOCUS]: {
            empty: NOTHING_TO_PROPOSE,
            singular:
                "Je vous propose de retirer 1 mail de Focus, confirmez à l'écran.",
            plural: "Je vous propose de retirer {count} mails de Focus, confirmez à l'écran.",
        },
    };
